import React, { useState } from 'react'
import { UserIcon } from '@heroicons/react/24/outline'
import { AvatarHelper } from '../utils/avatarHelper'

interface UserAvatarProps {
  /** 头像路径 */
  avatar?: string | null
  /** 用户名字 */
  firstName?: string
  /** 用户姓氏 */
  lastName?: string
  /** 头像尺寸 */
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl'
  /** 自定义className */
  className?: string
  /** 是否显示在线状态 */
  showOnline?: boolean
  /** 是否在线 */
  isOnline?: boolean
  /** 点击事件 */
  onClick?: () => void
  /** 是否显示边框 */
  bordered?: boolean
  /** 边框颜色 */
  borderColor?: 'white' | 'indigo' | 'slate'
  /** 是否显示阴影 */
  shadow?: boolean
  /** 是否使用渐变背景 */
  gradient?: boolean
}

const UserAvatar: React.FC<UserAvatarProps> = ({
  avatar,
  firstName,
  lastName,
  size = 'md',
  className = '',
  showOnline = false,
  isOnline = false,
  onClick,
  bordered = false,
  borderColor = 'white',
  shadow = false,
  gradient = false
}) => {
  const sizeClasses = {
    xs: 'h-6 w-6',
    sm: 'h-8 w-8',
    md: 'h-10 w-10',
    lg: 'h-12 w-12',
    xl: 'h-16 w-16',
    '2xl': 'h-24 w-24'
  }

  const iconSizeClasses = {
    xs: 'h-3 w-3',
    sm: 'h-4 w-4',
    md: 'h-5 w-5',
    lg: 'h-6 w-6',
    xl: 'h-8 w-8',
    '2xl': 'h-12 w-12'
  }

  const textSizeClasses = {
    xs: 'text-[10px]',
    sm: 'text-xs',
    md: 'text-sm',
    lg: 'text-base',
    xl: 'text-xl',
    '2xl': 'text-3xl'
  }

  const onlineDotSizes = {
    xs: 'h-1.5 w-1.5 ring-1',
    sm: 'h-2 w-2 ring-2',
    md: 'h-2.5 w-2.5 ring-2',
    lg: 'h-3 w-3 ring-2',
    xl: 'h-4 w-4 ring-[3px]',
    '2xl': 'h-5 w-5 ring-[3px]'
  }

  const borderClasses = {
    white: 'ring-white',
    indigo: 'ring-indigo-200',
    slate: 'ring-slate-200'
  }

  const avatarUrl = AvatarHelper.getAvatarUrl(avatar)
  const initials = AvatarHelper.getInitials(firstName, lastName)

  // 生成随机但一致的渐变色
  const getGradientClass = () => {
    if (!gradient) return 'bg-gradient-to-br from-slate-200 to-slate-300'

    const name = `${firstName || ''}${lastName || ''}`
    const hash = name.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0)
    const gradients = [
      'bg-gradient-to-br from-indigo-400 to-purple-500',
      'bg-gradient-to-br from-emerald-400 to-teal-500',
      'bg-gradient-to-br from-rose-400 to-pink-500',
      'bg-gradient-to-br from-amber-400 to-orange-500',
      'bg-gradient-to-br from-cyan-400 to-blue-500',
      'bg-gradient-to-br from-violet-400 to-fuchsia-500'
    ]
    return gradients[hash % gradients.length]
  }

  const baseClasses = `${sizeClasses[size]} rounded-full flex items-center justify-center overflow-hidden transition-all duration-200 ${className}`
  const cursorClass = onClick ? 'cursor-pointer hover:scale-105' : ''
  const borderClass = bordered ? `ring-2 ${borderClasses[borderColor]}` : ''
  const shadowClass = shadow ? 'shadow-lg' : ''

  const [imageError, setImageError] = useState(false)

  return (
    <div className={`relative inline-block ${cursorClass}`} onClick={onClick}>
      {avatarUrl && !imageError ? (
        <img
          src={avatarUrl}
          alt={`${firstName} ${lastName}`.trim() || '用户头像'}
          className={`${baseClasses} ${borderClass} ${shadowClass} object-cover`}
          onError={() => {
            console.error('头像加载失败:', avatarUrl)
            setImageError(true)
          }}
        />
      ) : (
        /* 默认头像背景 - 当没有头像或头像加载失败时显示 */
        <div className={`${baseClasses} ${borderClass} ${shadowClass} ${getGradientClass()}`}>
          {firstName || lastName ? (
            <span
              className={`${textSizeClasses[size]} font-bold ${gradient ? 'text-white' : 'text-slate-600'}`}
            >
              {initials}
            </span>
          ) : (
            <UserIcon className={`${iconSizeClasses[size]} ${gradient ? 'text-white/80' : 'text-slate-500'}`} />
          )}
        </div>
      )}

      {/* 在线状态指示器 */}
      {showOnline && (
        <div
          className={`absolute bottom-0 right-0 rounded-full ring-white ${onlineDotSizes[size]} ${
            isOnline
              ? 'bg-emerald-500'
              : 'bg-slate-400'
          }`}
        >
          {isOnline && (
            <span className="absolute inset-0 rounded-full bg-emerald-500 animate-ping opacity-75" />
          )}
        </div>
      )}
    </div>
  )
}

// 头像组组件
export const AvatarGroup: React.FC<{
  avatars: Array<{ avatar?: string | null; firstName?: string; lastName?: string }>
  max?: number
  size?: UserAvatarProps['size']
}> = ({ avatars, max = 4, size = 'md' }) => {
  const visibleAvatars = avatars.slice(0, max)
  const remainingCount = avatars.length - max

  return (
    <div className="flex -space-x-2">
      {visibleAvatars.map((user, index) => (
        <UserAvatar
          key={index}
          {...user}
          size={size}
          bordered
          borderColor="white"
          gradient
          className="relative"
          style={{ zIndex: visibleAvatars.length - index }}
        />
      ))}
      {remainingCount > 0 && (
        <div
          className={`${
            size === 'sm' ? 'h-8 w-8 text-xs' : 'h-10 w-10 text-sm'
          } rounded-full bg-slate-100 border-2 border-white flex items-center justify-center font-semibold text-slate-600`}
        >
          +{remainingCount}
        </div>
      )}
    </div>
  )
}

// 带名字的头像组件
export const AvatarWithName: React.FC<
  UserAvatarProps & {
    subtitle?: string
    namePosition?: 'right' | 'bottom'
  }
> = ({ subtitle, namePosition = 'right', firstName, lastName, ...props }) => {
  const fullName = `${firstName || ''} ${lastName || ''}`.trim()

  if (namePosition === 'bottom') {
    return (
      <div className="flex flex-col items-center gap-2">
        <UserAvatar {...props} firstName={firstName} lastName={lastName} />
        <div className="text-center">
          <div className="text-sm font-semibold text-slate-800">{fullName || '未知用户'}</div>
          {subtitle && <div className="text-xs text-slate-500">{subtitle}</div>}
        </div>
      </div>
    )
  }

  return (
    <div className="flex items-center gap-3">
      <UserAvatar {...props} firstName={firstName} lastName={lastName} />
      <div>
        <div className="text-sm font-semibold text-slate-800">{fullName || '未知用户'}</div>
        {subtitle && <div className="text-xs text-slate-500">{subtitle}</div>}
      </div>
    </div>
  )
}

export default UserAvatar
